import { MenuItem, Price, ProductChannel, WorkDay } from '@core/models';
import { isSimpleMenuItem } from '@core/domain/menu/rules';
import { HomeSingleProductOrderRule } from '../interfaces';
import { getPrimaryProductChannel } from '../../util';

export type SingleProductQuantityIcon = 'trash' | 'minus';

export function canInitializeSingleProduct(
	item: MenuItem | null,
	workDay: WorkDay | null
): item is MenuItem {
	return (
		item !== null &&
		workDay !== null &&
		Number.isInteger(workDay.precisionDecimal) &&
		workDay.precisionDecimal >= 0
	);
}

export function hasSingleProductModifierGroups(modifierGroupCount: number): boolean {
	return modifierGroupCount > 0;
}

export function hasMultipleSingleProductPrices(priceCount: number): boolean {
	return priceCount > 1;
}

export function hasSingleProductDiscount(
	price: bigint | number | string | null | undefined,
	specialPriceAmount: bigint | number | string | null | undefined
): boolean {
	return price !== specialPriceAmount;
}

export function getSingleProductQuantityIcon(quantity: number | string | null | undefined): SingleProductQuantityIcon {
	return Number(quantity ?? 1) <= 1 ? 'trash' : 'minus';
}

export function canDecreaseSingleProductQuantity(quantity: number | string | null | undefined): boolean {
	return Number(quantity ?? 1) > 1;
}

export function hasSuggestedSingleProductItems(items: MenuItem[] | null | undefined): boolean {
	return (items?.length ?? 0) > 0;
}

export function canIncreaseModifierQuantity(isSelected: boolean | null | undefined): boolean {
	return !!isSelected;
}

export function canDecreaseModifierQuantity(
	isSelected: boolean | null | undefined,
	quantity: number | bigint | string | null | undefined
): boolean {
	return !!isSelected && Number(quantity ?? 0) > 0;
}

export function isModifierSelectionDisabled(
	isSelected: boolean | null | undefined,
	maximumReached: boolean
): boolean {
	return !isSelected && maximumReached;
}

export function shouldShowModifierExtraPrice(quantity: number | bigint | string | null | undefined): boolean {
	return Number(quantity ?? 0) > 0;
}

export function shouldShowModifierBasePrice(isFree: boolean | null | undefined): boolean {
	return !isFree;
}

export function isModifierFree(isFree: boolean | null | undefined): boolean {
	return !!isFree;
}

export function mergeSingleProductChannel(
	originalChannel: ProductChannel | undefined,
	formChannel?: ProductChannel
): ProductChannel {
	const sourceChannel = formChannel ?? originalChannel;
	if (!sourceChannel) {
		throw new Error('A product channel is required to process a single-product order.');
	}

	return {
		...sourceChannel,
		...formChannel,
		imagen: formChannel?.imagen || originalChannel?.imagen,
	};
}

export function getSingleProductSpecialPriceAmount(
	prices: Price[],
	selectedPrice: bigint
): bigint {
	return prices.find(price => price.precio === selectedPrice)?.specialPriceAmount ?? selectedPrice;
}

export const homeSingleProductFormDataRule: HomeSingleProductOrderRule = context => {
	const { productosCanal: _formChannel, ...formValues } = context.formValues ?? {};
	return {
		...context,
		orderItem: {
			...(context.orderItem ?? {}),
			...formValues,
			detailsWithQuantity: context.detailsWithQuantity ?? [],
			detailsWithQuantityNoAdd: context.detailsWithQuantityNoAdd ?? [],
		},
	};
};

export const homeSingleProductResolveArticleRule: HomeSingleProductOrderRule = context => {
	const article = context.selectedArticle
		? context.cartItems?.find(item => item.idCartItem === context.selectedArticle?.idCartItem) ??
			context.selectedArticle
		: null;

	return { ...context, article };
};

export const homeSingleProductOriginalChannelRule: HomeSingleProductOrderRule = context => ({
	...context,
	originalChannel: getPrimaryProductChannel(context.article),
});

export const homeSingleProductChannelRule: HomeSingleProductOrderRule = context => {
	const channel = mergeSingleProductChannel(
		context.originalChannel,
		context.formValues?.productosCanal ?? context.formData?.productosCanal?.[0]
	);

	return {
		...context,
		channel,
		orderItem: { ...context.orderItem, productosCanal: [channel] },
	};
};

export const homeSingleProductQuantityRule: HomeSingleProductOrderRule = context => ({
	...context,
	orderItem: {
		...context.orderItem,
		cantidad: BigInt(Math.floor(Number(context.orderItem.cantidad ?? 1))),
	},
});

export const homeSingleProductSpecialPriceRule: HomeSingleProductOrderRule = context => ({
	...context,
	orderItem: {
		...context.orderItem,
		specialPriceAmount: getSingleProductSpecialPriceAmount(
			context.channel?.precios ?? [],
			context.orderItem.precio ?? 0n
		),
	},
});

export const homeSingleProductCartIdentityRule: HomeSingleProductOrderRule = context => {
	return {
		...context,
		orderItem: {
			...context.orderItem,
			idCartItem:
				context.orderItem.idCartItem ??
				(context.isSimpleProduct ? context.orderItem.idArticulo : context.generatedCartItemId),
		},
	};
};

export const homeSingleProductTypeRule: HomeSingleProductOrderRule = context => ({
	...context,
	isSimpleProduct: isSimpleMenuItem(context.orderItem),
});

export const homeSingleProductCartQuantityRule: HomeSingleProductOrderRule = context => {
	let quantity = context.orderItem.cantidad ?? 1n;

	if (context.isSimpleProduct && !context.isEditing) {
		const existingItem = context.cartItems?.find(
			item => item.idCartItem === context.orderItem.idArticulo
		);
		if (existingItem?.cantidad) {
			quantity += existingItem.cantidad;
		}
	}

	return {
		...context,
		orderItem: { ...context.orderItem, cantidad: quantity },
	};
};

export const homeSingleProductMergeArticleRule: HomeSingleProductOrderRule = context => ({
	...context,
	orderItem: { ...(context.article ?? {}), ...context.orderItem },
});

export function withDefaultSingleProductQuantity(item: MenuItem): MenuItem {
	return { ...item, cantidad: item.cantidad || 1n };
}
